function PathCard({ href, icon, label, title, desc, btnText, variant, delay }) {
  const btnStyles = {
    mint: "bg-mint text-ink",
    gold: "bg-gold text-ink",
    ghost:
      "bg-[rgba(247,242,233,.07)] border border-[rgba(247,242,233,.15)] text-t-sec",
  };
  const hoverBorder = {
    mint: "hover:border-[rgba(109,211,167,.28)]",
    gold: "hover:border-[rgba(242,185,73,.28)]",
    ghost: "hover:border-[rgba(109,211,167,.28)]",
  };
  const accentColor = {
    mint: "#6DD3A7",
    gold: "#F2B949",
    ghost: "#3D6278",
  };

  return (
    <a
      href={href}
      target={href.startsWith("mailto") ? undefined : "_blank"}
      rel={href.startsWith("mailto") ? undefined : "noopener"}
      className={[
        "group relative flex flex-col items-center gap-2.5 text-center rounded-[14px]",
        "bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] overflow-hidden",
        "p-[clamp(18px,2.2vw,28px)_clamp(14px,1.6vw,20px)] no-underline text-inherit",
        "transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(8,24,40,.55)]",
        hoverBorder[variant],
        "anim-up",
      ].join(" ")}
      style={{ animationDelay: `${delay}s` }}
    >
      {/* accent top line */}
      <span
        className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 origin-center transition-transform duration-[350ms] group-hover:scale-x-100"
        style={{ background: accentColor[variant] }}
      />

      <span className="text-[1.6rem] leading-none">{icon}</span>
      <span className="font-mono text-[.52rem] tracking-[.12em] uppercase text-t-dim">
        {label}
      </span>
      <p
        className="font-cairo font-extrabold text-t-primary leading-[1.2]"
        style={{ fontSize: "clamp(.88rem,1vw,1rem)" }}
      >
        {title}
      </p>
      <p className="text-[.74rem] text-t-sec leading-[1.6]">{desc}</p>

      <span
        className={`inline-flex items-center gap-1.5 font-cairo font-bold text-[.78rem] px-4 py-2 rounded-[4px] mt-auto transition-opacity duration-200 group-hover:opacity-90 ${btnStyles[variant]}`}
      >
        <svg
          className="w-[13px] h-[13px] flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {btnText}
      </span>
    </a>
  );
}

const PATHS = [
  {
    href: "https://store.afaaqwetaan.com",
    icon: "✈️",
    label: "GATE 01 · BOARDING",
    variant: "mint",
    title: "جاهز تبدأ الرحلة",
    desc: "استعرض كل البرامج في المتجر واحجز مقعدك مباشرة. دفع كامل أو تقسيط عبر تمارا.",
    btnText: "استعرض البرامج",
    delay: 0.36,
  },
  {
    href: "https://linktr.ee/AfaaqWetaan",
    icon: "🌐",
    label: "GATE 02 · LOUNGE",
    variant: "gold",
    title: "لسه تفكّر",
    desc: "انضم لمجتمع آفاق مجاناً — اقرأ تجارب المتدربين وشارك في التحديات واتخذ قرارك بوقتك.",
    btnText: "انضم مجاناً",
    delay: 0.42,
  },
  {
    href: "mailto:afaaq@wetaan.com",
    icon: "💬",
    label: "GATE 03 · INFO DESK",
    variant: "ghost",
    title: "عندك سؤال",
    desc: "تواصل مع فريقنا مباشرة وسيساعدك تختار البرنامج الأنسب لمستواك وهدفك.",
    btnText: "تواصل معنا",
    delay: 0.48,
  },
];

const TRUST = [
  "دفع بالتقسيط عبر تمارا",
  "تدريب عن بُعد",
  "شهادة إتمام معتمدة",
  "مجتمع مجاني للجميع",
];

export default function CTA() {
  return (
    <div
      className="relative overflow-hidden bg-ink-deep border-t border-[rgba(109,211,167,.1)]
      px-[clamp(16px,5vw,80px)] py-[clamp(56px,8vh,96px)]"
      id="cta"
    >
      {/* runway glow bg */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[900px] h-1/2 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 100%,rgba(109,211,167,0.07) 0%,transparent 70%),radial-gradient(ellipse 30% 20% at 50% 100%,rgba(242,185,73,0.05) 0%,transparent 60%)",
        }}
      />

      {/* centreline dashes */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[2px] h-[40%] pointer-events-none"
        style={{
          background:
            "repeating-linear-gradient(180deg,rgba(242,185,73,.25) 0,rgba(242,185,73,.25) 12px,transparent 12px,transparent 22px)",
        }}
      />

      <div className="relative z-[2] text-center max-w-[680px] mx-auto">
        {/* tag */}
        <span
          className="inline-flex items-center gap-2 font-mono text-[.66rem] tracking-[.2em] uppercase text-gold mb-[1.4rem] before:content-['◆'] before:text-[.4rem] before:opacity-50 after:content-['◆'] after:text-[.4rem] after:opacity-50 anim-up"
          style={{ animationDelay: ".1s" }}
        >
          READY FOR TAKEOFF · جاهز للإقلاع؟
        </span>

        {/* headline */}
        <h2
          className="font-cairo font-black leading-[1.08] tracking-tight text-t-primary mb-[.7rem] anim-up"
          style={{
            fontSize: "clamp(2rem,4vw,3.6rem)",
            letterSpacing: "-.03em",
            animationDelay: ".2s",
          }}
        >
          وين أنت
          <br />
          من هذي <em className="not-italic text-mint">الخريطة؟</em>
        </h2>

        {/* sub */}
        <p
          className="text-t-sec leading-[1.85] mb-[clamp(32px,4vh,48px)] anim-up"
          style={{
            fontSize: "clamp(.9rem,.97rem,1.05rem)",
            animationDelay: ".28s",
          }}
        >
          كل شخص يصل لهذه الصفحة في نقطة مختلفة من رحلته.{" "}
          <strong className="text-t-primary">
            لكل واحد منكم عندنا خطوة مناسبة.
          </strong>
        </p>

        {/* path cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[clamp(10px,1.2vw,14px)] mb-[clamp(24px,3vh,36px)]">
          {PATHS.map((p) => (
            <PathCard key={p.label} {...p} />
          ))}
        </div>

        {/* trust badges */}
        <div
          className="flex items-center justify-center gap-[clamp(12px,2vw,24px)] flex-wrap anim-up"
          style={{ animationDelay: ".44s" }}
        >
          {TRUST.map((item, i) => (
            <span key={item} className="flex items-center gap-1.5">
              {i > 0 && <span className="w-px h-3 bg-t-dim opacity-40" />}
              <span className="flex items-center gap-1.5 font-mono text-[.72rem] text-t-dim">
                <span className="text-[.42rem] text-mint">◆</span>
                {item}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
